import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white/[0.03] border border-white/15 rounded-none px-4 py-3.5 font-mono text-sm text-white placeholder:text-white/30 outline-none focus:border-thermal focus:shadow-[0_0_0_1px_#FF5A00,0_0_24px_rgba(255,90,0,0.25)] transition-[border-color,box-shadow] duration-300";

const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", company: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/demo-requests`, form);
      toast.success(t.contact.success);
      setForm({ name: "", company: "", email: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="border-b border-white/10" data-testid="contact-section">
      <div className="px-5 md:px-10 py-24 md:py-36 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-[10px] md:text-xs tracking-[0.35em] text-thermal mb-6" data-testid="contact-label">
            {t.contact.label}
          </p>
          <h2 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mb-8" data-testid="contact-title">
            {t.contact.title}
          </h2>
          <p className="text-sm md:text-base text-white/60 leading-relaxed max-w-md" data-testid="contact-body">
            {t.contact.body}
          </p>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="space-y-5"
          data-testid="demo-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <input name="name" required value={form.name} onChange={onChange} placeholder={t.contact.name} className={inputCls} data-testid="form-name-input" />
            <input name="company" required value={form.company} onChange={onChange} placeholder={t.contact.company} className={inputCls} data-testid="form-company-input" />
          </div>
          <input name="email" type="email" required value={form.email} onChange={onChange} placeholder={t.contact.email} className={inputCls} data-testid="form-email-input" />
          <textarea name="message" required rows={5} value={form.message} onChange={onChange} placeholder={t.contact.message} className={`${inputCls} resize-none`} data-testid="form-message-input" />
          <button
            type="submit"
            disabled={sending}
            className="w-full bg-thermal text-black font-mono text-sm tracking-[0.25em] py-4 hover:bg-white disabled:opacity-60 transition-colors duration-300"
            data-testid="contact-form-submit"
          >
            {(sending ? t.contact.sending : t.contact.submit).toUpperCase()}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
